import { formatCredentialDate } from '~/features/utils';
import { EXPIRING_SOON_DAYS } from './constants';
import type { AlertItem, CategoryStatus, CategorySummary } from './types';

export type EmploymentRecord = {
  id?: number;
  startDate: string;
  endDate?: string | null;
  currentFlag?: boolean;
  employer?: string;
  title?: string;
  department?: string;
  apptType?: { label?: string } | string | null;
};

export type BoardCertificationRecord = {
  origCertDate?: string | null;
};

export type AcademicRecord = {
  gradDate?: string | null;
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const parseDateTime = (date?: string | null): number | null => {
  if (!date) return null;
  const time = new Date(date).getTime();
  return Number.isNaN(time) ? null : time;
};

export const getEarliestOriginalCertificationDate = (
  boardCertifications: BoardCertificationRecord[]
): string | undefined => {
  return boardCertifications
    .map((cert) => cert.origCertDate)
    .filter((date): date is string => Boolean(date) && parseDateTime(date) !== null)
    .sort((a, b) => parseDateTime(a)! - parseDateTime(b)!)[0];
};

const academicRecency = (date?: string | null): number => {
  if (!date || date === 'Incomplete') return Number.POSITIVE_INFINITY;
  return parseDateTime(date) ?? Number.NEGATIVE_INFINITY;
};

export const getMostRecentAcademicRecords = <T extends AcademicRecord>(
  records: T[],
  limit = 5
): T[] => {
  return records
    .map((record, index) => ({ record, index }))
    .sort((a, b) => {
      const diff = academicRecency(b.record.gradDate) - academicRecency(a.record.gradDate);
      return diff === 0 ? a.index - b.index : diff;
    })
    .slice(0, limit)
    .map(({ record }) => record);
};

const employmentRecency = (employment: EmploymentRecord): number => {
  if (employment.currentFlag || !employment.endDate) return Number.POSITIVE_INFINITY;
  return parseDateTime(employment.endDate) ?? Number.NEGATIVE_INFINITY;
};

export const getMostRecentEmploymentRecords = <T extends EmploymentRecord>(
  employments: T[],
  limit = 5
): T[] => {
  return employments
    .filter((employment) => employment.startDate)
    .map((employment, index) => ({ employment, index }))
    .sort((a, b) => {
      const diff = employmentRecency(b.employment) - employmentRecency(a.employment);
      return diff === 0 ? a.index - b.index : diff;
    })
    .slice(0, limit)
    .map(({ employment }) => employment);
};

export const detectEmploymentGaps = (
  employments: EmploymentRecord[],
  scanStartDate?: string,
  scanEndDate?: string
): boolean => {
  const valid = employments.filter((e) => e.startDate);
  if (valid.length < 2 && !scanStartDate) return false;

  const sorted = [...valid].sort(
    (a, b) => (parseDateTime(a.startDate) ?? 0) - (parseDateTime(b.startDate) ?? 0)
  );

  let maxEnd = parseDateTime(scanStartDate) ?? 0;
  for (const curr of sorted) {
    const currStart = parseDateTime(curr.startDate);
    if (currStart == null) continue;
    if (maxEnd !== 0 && currStart - maxEnd > THIRTY_DAYS_MS) return true;

    if (curr.currentFlag) {
      maxEnd = Number.POSITIVE_INFINITY;
    } else if (curr.endDate) {
      const currEnd = parseDateTime(curr.endDate);
      if (currEnd != null) maxEnd = Math.max(maxEnd, currEnd);
    }
  }
  const scanEnd = scanStartDate ? parseDateTime(scanEndDate ?? new Date().toISOString()) : null;
  if (scanEnd != null && maxEnd !== 0 && maxEnd !== Number.POSITIVE_INFINITY) {
    return scanEnd - maxEnd > THIRTY_DAYS_MS;
  }
  return false;
};

// [MCV-1811] A dashboard card shows a credential date as the grids do (formatCredentialDate):
// Jan 1, the stored form of a year-only CV date, is just the year ("1993"); any other date is
// "Jul 1999". No precision is stored with the date, so a Jan 1 a provider typed in also shows as
// the year, as it does in the grids. A timestamp is read by its date part, and a date that is not on the calendar
// (2024-02-31, which `new Date` rolls into March) shows "--".
export const formatCredentialSummaryDate = (date: string): string =>
  formatCredentialDate(date.slice(0, 10)) || '--';

export type ExpirationResult = 'expired' | 'expiring' | 'inactive' | 'active' | 'unknown';

// Parse YYYY-MM-DD as local midnight. `new Date('2026-04-12')` parses as UTC
// midnight, which becomes the previous day in negative UTC offsets and causes
// same-day expirations to compare as past. Full ISO strings with time/offset
// fall through to the default parser.
const parseExpiryAsLocalDate = (expiryDate: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiryDate);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(expiryDate);
};

export const computeExpirationStatus = (
  expiryDate: string | undefined | null
): ExpirationResult => {
  if (!expiryDate) return 'active';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiry = parseExpiryAsLocalDate(expiryDate);
  if (expiry < startOfToday) return 'expired';
  const threshold = new Date(startOfToday);
  threshold.setDate(startOfToday.getDate() + EXPIRING_SOON_DAYS);
  if (expiry <= threshold) return 'expiring';
  return 'active';
};

export const sortItemsByUrgency = <T extends { status: ExpirationResult }>(
  items: T[],
  max = 5
): T[] => {
  const priority: Record<ExpirationResult, number> = {
    expired: 0,
    inactive: 1,
    expiring: 2,
    unknown: 3,
    active: 4,
  };
  return [...items].sort((a, b) => priority[a.status] - priority[b.status]).slice(0, max);
};

export const computeCategoryStatus = (
  expiredCount: number,
  expiringCount: number,
  unknownCount = 0,
  inactiveCount = 0
): CategoryStatus => {
  if (expiredCount > 0) return 'error';
  if (expiringCount > 0 || unknownCount > 0 || inactiveCount > 0) return 'warning';
  return 'good';
};

export const computeAlerts = (
  categories: Pick<CategorySummary, 'name' | 'status' | 'statusText' | 'navigateTo'>[]
): AlertItem[] => {
  return categories
    .filter((c) => c.status === 'error' || c.status === 'warning')
    .map((c) => ({
      category: c.name,
      message: c.statusText,
      navigateTo: c.navigateTo,
    }));
};

export const computeCompleteness = (
  counts: { name: string; count: number }[]
): {
  percentage: number;
  verified: number;
  total: number;
  categories: { name: string; verified: boolean }[];
} => {
  const categories = counts.map((c) => ({
    name: c.name,
    verified: c.count > 0,
  }));
  const verified = categories.filter((c) => c.verified).length;
  const total = categories.length;
  return {
    percentage: total === 0 ? 0 : Math.round((verified / total) * 100),
    verified,
    total,
    categories,
  };
};

export const computeQuickStats = (
  total: number,
  active: number,
  expiringSoon: number,
  expired: number,
  inactive: number
) => ({ total, active, expiringSoon, expired, inactive });
